import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CBadge, CCol, CFormCheck, CFormInput, CFormSelect, CRow, CFormLabel, CModal, CModalHeader, CModalBody } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTrash, faClose } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useTranslationContext } from "@/context/TranslationContext";
import { titles } from "@/libs/constant";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import routes from "../../../libs/routes";
import { setFormatDate } from "@/utils/dateFormate";
import { useListUser, useUpdateUserStatus, useDeleteUser, useGrantPremiumReward } from "@/hooks/useUserMaster";
import { useGetSettingData } from "@/hooks";
import userTranslations from "../../../constants/translation/userMaster.translation";
import UserMasterValidationClass from "@/helpers/validations/userMaster";
import { options } from "@/constants/translation/common.translation";
import { CustomInput, TNTable } from "@/components";
import CustomDropdown from "@/components/CustomDropdown";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Formik } from "formik";
import constants from "@/libs/constant";

const userMaster = routes?.userMaster;
const user = userTranslations;
const { status, sort, dateTypes } = constants;

const isStatusActive = (value) =>
  value === status.apiStatus.active || value === status.apiStatus.true;

/**
 * ListUserPage Component
 * - Displays a list of users with options to edit, view, delete, and update status.
 * - Handles state management with local storage for filters and pagination.
 * - Uses APIs to delete and update user status.
 * - Renders user data in a table with action buttons.
 */
const ListUserPage = () => {
  const navigate = useNavigate();
  const t = useTranslationContext();

  // Local states
  const [userList, setUserList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  // Grant Premium Reward: selection is transient, page-local UI state (not Redux/React Query)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [adminStatus, setAdminStatus] = useState("");
  const [userStatus, setUserStatus] = useState("");
  const [signupType, setSignupType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Search timeout
  const [tempSearchText, setTempSearchText] = useState("");
  const searchTimeout = useRef(null);
  const dateTimeout = useRef(null);
  const searchInputRef = useRef(null);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const isFiltering = useRef(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [tableKey, setTableKey] = useState(0);

  // Clear localStorage and reset state when component unmounts
  useEffect(() => {
    return () => {
      // Clear localStorage
      delete localStorage.adminUserList;
      
      // Clear timeouts
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (dateTimeout.current) {
        clearTimeout(dateTimeout.current);
      }
    };
  }, []);

  // Status options
  const adminStatusOptions = constants.status.adminStatusOptions.map((option, index) => {
    if (index === constants.optionIndexes.DEFAULT) return { ...option, label: t(user.list.admin_status_option) };
    if (index === constants.optionIndexes.ADMIN_INACTIVE) return { ...option, label: t(user.view.status_badges.inactive) };
    if (index === constants.optionIndexes.ADMIN_ACTIVE) return { ...option, label: t(user.view.status_badges.active) };
    return option;
  });

  const userStatusOptions = constants.status.userStatusOptions.map((option, index) => {
    if (index === constants.optionIndexes.DEFAULT) return { ...option, label: t(user.list.user_status_option) };
    if (index === constants.optionIndexes.USER_ACTIVE) return { ...option, label: t(user.view.status_badges.active) };
    if (index === constants.optionIndexes.USER_INACTIVE) return { ...option, label: t(user.view.status_badges.inactive) };
    if (index === constants.optionIndexes.USER_DELETED) return { ...option, label: t(user.view.status_badges.deleted) };
    return option;
  });

  const isDeletedStatusFilter = userStatus === constants.status.apiStatus.deleted;

  const signupTypeOptions = constants.status.signupTypeOptions.map((option, index) => {
    if (index === constants.optionIndexes.DEFAULT) return { ...option, label: t(user.list.signup_type_option) };
    if (index === constants.optionIndexes.SIGNUP_NORMAL) return { ...option, label: t(user.list.signup_types.normal) };
    if (index === constants.optionIndexes.SIGNUP_GOOGLE) return { ...option, label: t(user.list.signup_types.google) };
    if (index === constants.optionIndexes.SIGNUP_APPLE) return { ...option, label: t(user.list.signup_types.apple) };
    return option;
  });

  /**
   * Handle search with debounce
   */
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      setSearchText(searchValue);
      setCurrentPage(1);
      setTableKey(prev => prev + 1); // Force table re-render
      
      // Clear the flag after a short delay to prevent TNTable pagination
      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 400); // Delay before updating real searchText
  };

  useEffect(() => {
    // Clear timeouts on unmount
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (dateTimeout.current) {
        clearTimeout(dateTimeout.current);
      }
    };
  }, []);

  const handleDateChange = (dateType, value) => {
    if (dateTimeout.current) {
      clearTimeout(dateTimeout.current);
    }

    dateTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      if (dateType === dateTypes.start) {
        // Handle empty string (when clear is clicked)
        if (value === "") {
          setStartDate("");
        } else {
          setStartDate(value);
          // If endDate is before new startDate, update endDate too
          if (endDate && endDate < value) {
            setEndDate(value);
          }
        }
      } else {
        // Handle empty string (when clear is clicked)
        if (value === "") {
          setEndDate("");
        } else {
          // If endDate < startDate, set it as startDate instead
          if (value < startDate) {
            setEndDate(startDate);
          } else {
            setEndDate(value);
          }
        }
      }
      setCurrentPage(1);
      setTableKey(prev => prev + 1); // Force table re-render
      
      // Clear the flag after a short delay
      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 300); // Debounce date changes
  };
  const handleDateBlur = (dateType, e) => {
    // If the input is cleared (empty value), ensure the state is also cleared
    if (e.target.value === "") {
      if (dateType === dateTypes.start) {
        setStartDate("");
      } else {
        setEndDate("");
      }
      setCurrentPage(1); // Reset to first page when clearing date
    }
  };

  const handleDateClick = (dateType, e) => {
    // Store current value to detect if clear was clicked
    const currentValue = e.target.value;
    setTimeout(() => {
      if (e.target.value === "" && currentValue !== "") {
        // Clear was clicked
        if (dateType === dateTypes.start) {
          setStartDate("");
        } else {
          setEndDate("");
        }
        setCurrentPage(1); // Reset to first page when clearing date
      }
    }, 100);
  };
    const handleClearFilter = () => {
    isFiltering.current = true;
    
    setSearchText("");
    setAdminStatus("");
    setUserStatus("");
    setSignupType("");
    setStartDate("");
    setEndDate("");
    setTempSearchText("");
    setCurrentPage(1);
    setTableKey(prev => prev + 1); // Force table re-render
    
    // Clear the flag after a short delay
    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  /**
   * Handle clear filters
   */
  const handleSort = (field) => {
    isFiltering.current = true;
    
    if (sortField === field) {
      setSortOrder(sortOrder === sort.order.asc ? sort.order.desc : sort.order.asc);
    } else {
      setSortField(field);
      setSortOrder(sort.order.asc);
    }
    setCurrentPage(1); // Reset to first page when sorting
    setTableKey(prev => prev + 1); // Force table re-render
    
    // Clear the flag after a short delay
    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };
  /**
   * API call for user list
   */
  const {
    data: listData,
    isLoading,
    refetch,
  } = useListUser({
    page: currentPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && { search: searchText }),
    ...(adminStatus && { admin_status: adminStatus }),
    ...(userStatus && { status: userStatus }),
    ...(signupType && { signup_type: signupType }),
    ...(startDate && { date_from: startDate }),
    ...(endDate && { date_to: endDate }),
    sort_by: sortField,
    order_by: sortOrder,
  });

// Update local state when API data changes
useEffect(() => {
  if (listData) {
    setUserList(listData.data.users || []);
    setPaginationData({
      ...listData.data.pagination,
      count: listData.data.pagination.total,
    });
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }
}, [listData]);

  /**
   * API mutations
   */
  const { mutate: doUpdateStatus } = useUpdateUserStatus(
    () => {
      refetch();
    }
  );

  const { mutate: doDeleteUser } = useDeleteUser(
    () => {
      refetch();
    }
  );

  // Reused as-is from the Settings page, per the implementation plan, so this list doesn't
  // duplicate the setting/list API call just to read the configured months value.
  const { data: settingsData } = useGetSettingData();
  const configuredPremiumMonths = settingsData?.data?.premium_grant_months;

  const { mutate: doGrantPremiumReward } = useGrantPremiumReward(() => {
    setSelectedIds(new Set());
    refetch();
  });

  /**
   * Selection handlers for the "Grant Premium Reward" bulk action checkboxes
   */
  const handleToggleSelectRow = (uuid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const handleToggleSelectAllOnPage = (pageUuids) => {
    setSelectedIds((prev) => {
      const allSelected = pageUuids.length > 0 && pageUuids.every((uuid) => prev.has(uuid));
      const next = new Set(prev);
      if (allSelected) {
        pageUuids.forEach((uuid) => next.delete(uuid));
      } else {
        pageUuids.forEach((uuid) => next.add(uuid));
      }
      return next;
    });
  };

  /**
   * Handle "Grant Premium Reward" bulk action click
   */
  const handleGrantPremiumRewardClick = () => {
    const numericMonths = Number(configuredPremiumMonths);
    const isValidMonths =
      configuredPremiumMonths !== undefined &&
      configuredPremiumMonths !== null &&
      configuredPremiumMonths !== "" &&
      Number.isInteger(numericMonths) &&
      numericMonths >= 1;

    if (!isValidMonths) {
      toast.error(t(user.list.premium_reward.months_not_configured_message));
      return;
    }

    const selectedUuids = Array.from(selectedIds);

    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-box">
            <div className="alert-popup">
              <FontAwesomeIcon
                className="alert-close"
                icon={faClose}
                onClick={onClose}
              />
              <h2 className="alert-title">
                {t(user.list.premium_reward.confirm_heading)}
              </h2>
              <p className="text-center mb-3">
                {t(user.list.premium_reward.confirm_message)
                  .replace("{{months}}", numericMonths)
                  .replace("{{count}}", selectedUuids.length)}
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={() => {
                    doGrantPremiumReward(selectedUuids);
                    onClose();
                  }}
                >
                  {t(user.list.premium_reward.confirm_yes_text)}
                </CButton>
                <CButton
                  className="table-primary-button"
                  color="danger"
                  onClick={onClose}
                >
                  {t(user.list.premium_reward.confirm_no_text)}
                </CButton>
              </div>
            </div>
          </div>
        );
      },
    });
  };

  /**
   * Handle view click
   */
  const handleViewClick = (userId) => {
    navigate(userMaster.view.replace(":id", userId));
  };

  /**
   * Handle delete click
   */
  const handleDeleteClick = (userId) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-box">
            <div className="alert-popup">
            <FontAwesomeIcon
              className="alert-close"
              icon={faClose}
              onClick={onClose}
            />
              <h2 className="alert-title">
                {t(user.list.delete_user_heading_text)}
              </h2>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={() => {
                    doDeleteUser(userId);
                    onClose();
                  }}
                >
                  {t(user.list.delete_user_yes_text)}
                </CButton>
                <CButton
                  className="table-primary-button"
                  color="danger"
                  onClick={onClose}
                >
                  {t(user.list.delete_user_no_text)}
                </CButton>
              </div>
            </div>
          </div>
        );
      },
    });
  };

  /**
   * Handle status click
   */
  const handleStatusClick = (userId, type, currentStatus, adminStatus) => {
    if (type === "admin") {
      // Simple confirmation for both activation and deactivation
      // Handle API values: "active", "deactivated", "true"
      const newStatus = (currentStatus === constants.status.apiStatus.active || currentStatus === constants.status.apiStatus.true) ? constants.status.apiStatus.deactivated : constants.status.apiStatus.active;
      
      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <div className="alert-box admin-status-popup">
              <div className="alert-popup">
                <FontAwesomeIcon
                  className="alert-close"
                  icon={faClose}
                  onClick={onClose}
                />
                <h2 className="alert-title">
                  {t(user.list.update_popup_admin_heading_text)}
                </h2>
                <div className="popup-button-container">
                  <CButton
                    color="primary"
                    className="table-delete-button"
                    onClick={() => {
                      const updateData = { id: userId, type, status: newStatus };
                      doUpdateStatus(updateData);
                      onClose();
                      setCurrentPage(1);
                    }}
                  >
                    {t(user.list.update_popup_yes_btn_text)}
                  </CButton>
                  <CButton
                    className="table-primary-button"
                    color="danger"
                    onClick={onClose}
                  >
                    {t(user.list.update_popup_no_btn_text)}
                  </CButton>
                </div>
              </div>
            </div>
          );
        },
      });
    } else {
      const isActivatingUser = !isStatusActive(currentStatus);
      const isAdminInactive = !isStatusActive(adminStatus);

      if (isActivatingUser && isAdminInactive) {
        toast.warning(t(user.list.user_activate_blocked_admin_inactive));
        return;
      }

      // Handle user status - use API values: "active", "inactive"
      const newStatus = isStatusActive(currentStatus) ? status.apiStatus.inactive : status.apiStatus.active;
      
      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <div className="alert-box">
              <div className="alert-popup">
              <FontAwesomeIcon
                  className="alert-close"
                  icon={faClose}
                  onClick={onClose}
                />
                <h2 className="alert-title">
                  {t(user.list.update_popup_user_heading_text)}
                </h2>
                <div className="popup-button-container">
                  <CButton
                    color="primary"
                    className="table-delete-button"
                    onClick={() => {
                      const updateData = { id: userId, type, status: newStatus };
                      doUpdateStatus(updateData);
                      onClose();
                      setCurrentPage(1);
                    }}
                  >
                    {t(user.list.update_popup_yes_btn_text)}
                  </CButton>
                  <CButton
                    className="table-primary-button"
                    color="danger"
                    onClick={onClose}
                  >
                    {t(user.list.update_popup_no_btn_text)}
                  </CButton>
                </div>
              </div>
            </div>
          );
        }
      });
    }
  };

  /**
   * Table columns definition
   */
  const columns = useMemo(() => {
    const currentPageUuids = userList.map((row) => row.uuid);
    const allOnPageSelected =
      currentPageUuids.length > 0 && currentPageUuids.every((uuid) => selectedIds.has(uuid));

    return [
    {
      id: "select",
      header: () => (
        <CFormCheck
          checked={allOnPageSelected}
          onChange={() => handleToggleSelectAllOnPage(currentPageUuids)}
        />
      ),
      cell: ({ row }) => (
        <CFormCheck
          checked={selectedIds.has(row.original.uuid)}
          onChange={() => handleToggleSelectRow(row.original.uuid)}
        />
      ),
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: t(user.list.table.created_at_table_heading),
      cell: ({ getValue }) => <span>{setFormatDate(getValue())}</span>,
    },
    {
      id: "email",
      accessorKey: "email",
      header: () => (
        <div onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
          {t(user.list.table.email_table_heading)}
        </div>
      ),
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue() || t(user.list.defaults.na)}</span>,
    },
    {
      id: "full_name",
      accessorKey: "full_name",
      header: () => (
        <div
          onClick={() => handleSort("full_name")}
          style={{ cursor: "pointer" }}
        >
          {t(user.list.table.full_name_heading)}
        </div>
      ),
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue() || t(user.list.defaults.na)}</span>,
    },
    {
      id: "contact_number",
      accessorKey: "contact_number",
      header: t(user.list.table.contact_number_heading),
      cell: ({ getValue }) => <span>{getValue() || t(user.list.defaults.na)}</span>,
    },
    {
      id: "user_name",
      accessorKey: "user_name",
      header: t(user.list.table.username_heading),
      cell: ({ getValue }) => <span>{getValue() || t(user.list.defaults.na)}</span>,
    },
    {
      id: "admin_status",
      accessorKey: "admin_status",
      header: t(user.list.table.admin_status_heading),
      cell: ({ row }) => {
        const status = row.original.admin_status;
        let statusText = t(user.view.status_badges.inactive);
        let statusClass = "text-danger";
        
        // Handle API values: "active", "deactivated", "true"
        if (status === constants.status.apiStatus.active || status === constants.status.apiStatus.true) {
          statusText = t(user.view.status_badges.active);
          statusClass = "text-success";
        } else if (status === constants.status.apiStatus.deactivated) {
          statusText = t(user.view.status_badges.inactive);
          statusClass = "text-danger";
        }
        
        return (
          <span
            className={isDeletedStatusFilter ? statusClass : `${statusClass} cursor-pointer`}
            onClick={isDeletedStatusFilter ? undefined : () => handleStatusClick(row.original.uuid, "admin", status)}
          >
            {statusText}
          </span>
        );
      },
    },
    {
      id: "user_status",
      accessorKey: "is_active",
      header: t(user.list.table.user_status_heading),
      cell: ({ row }) => {
        const status = row.original.is_active;
        // Handle API values: "active", "inactive", "true"
        const isActive = status === constants.status.apiStatus.active || status === constants.status.apiStatus.true;
        const statusClass = isActive ? "text-success" : "text-danger";

        return (
          <span
            className={isDeletedStatusFilter ? statusClass : `${statusClass} cursor-pointer`}
            onClick={isDeletedStatusFilter ? undefined : () => handleStatusClick(row.original.uuid, "user", status, row.original.admin_status)}
          >
            {isActive ? t(user.view.status_badges.active) : t(user.view.status_badges.inactive)}
          </span>
        );
      },
    },
    ...(isDeletedStatusFilter
      ? [
          {
            id: "deleted_at",
            accessorKey: "deleted_at",
            header: t(user.list.table.deleted_at_heading),
            cell: ({ getValue }) => <span>{getValue() ? setFormatDate(getValue()) : t(user.list.defaults.na)}</span>,
          },
        ]
      : []),
    {
      id: "signup_type",
      accessorKey: "signup_type",
      header: t(user.list.table.signup_type_heading),
      cell: ({ getValue }) => (
        <span>
          {getValue() ? 
            getValue().charAt(0).toUpperCase() + getValue().slice(1) 
            : t(user.list.defaults.na)
          }
        </span>
      ),
    },
    {
      id: "app_version",
      accessorKey: "app_version",
      header: t(user.list.table.app_version_heading),
      cell: ({ getValue }) => <span>{getValue() ?? t(user.list.defaults.na)}</span>,
    },
    {
      id: "premium_grant_count",
      accessorKey: "premium_grant_count",
      header: t(user.list.table.reward_count_heading),
      cell: ({ getValue }) => <span>{getValue() ?? 0}</span>,
    },
    {
      id: "action",
      accessorKey: "id",
      header: t(user.list.table.action_table_col_heading),
      cell: ({ row }) => {
        return (
          <div className="d-flex justify-content-center gap-3">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="view-tooltip">{t(user.list.table.view_tooltip)}</Tooltip>}
            >
              <CButton
                className="filter-field-space"
                onClick={() => handleViewClick(row.original.uuid)}
                style={{
                  backgroundColor: '#212631',
                  borderColor: '#212631',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <FontAwesomeIcon icon={faEye} />
              </CButton>
            </OverlayTrigger>
          </div>
        );
      },
    },
  ];
  }, [
    t,
    handleSort,
    handleStatusClick,
    handleViewClick,
    isDeletedStatusFilter,
    userList,
    selectedIds,
    handleToggleSelectAllOnPage,
    handleToggleSelectRow,
  ]);


  // Show shimmer loader for all loading states

  return (
    <>
      {/* Filters */}
      <CRow className="mb-4">
        <CCol lg={12} className="user-list-filters d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            ref={searchInputRef}
            placeholder={t(user.list.search_placeholder)}
            onKeyUp={handleSearch}
            onChange={handleSearch}
            value={tempSearchText}
            className="filter-field-space form-control-sm same-input-cls"
            style={{ 
              maxWidth: '300px',
              backgroundColor: '#212631',
              borderColor: '#212631',
              color: 'white'
            }}
          />
          
          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ 
              maxWidth: '200px', 
              backgroundColor: '#212631', 
              borderColor: '#212631', 
              color: 'white'
            }}
            value={adminStatus}
            onChange={(e) => {
              isFiltering.current = true;
              setAdminStatus(e.target.value);
              setCurrentPage(1);
              setTableKey(prev => prev + 1); // Force table re-render
              
              // Clear the flag after a short delay
              setTimeout(() => {
                isFiltering.current = false;
              }, 200);
            }}
            options={adminStatusOptions}
            placeholder={t(user.list.admin_status_option)}
          />
          
          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: '200px', backgroundColor: '#212631', borderColor: '#212631', color: 'white' }}
            value={userStatus}
            onChange={(e) => {
              isFiltering.current = true;
              setUserStatus(e.target.value);
              setCurrentPage(1);
              setTableKey(prev => prev + 1); // Force table re-render
              
              // Clear the flag after a short delay
              setTimeout(() => {
                isFiltering.current = false;
              }, 200);
            }}
            options={userStatusOptions}
            placeholder={t(user.list.user_status_option)}
          />
          
          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: '200px', backgroundColor: '#212631', borderColor: '#212631', color: 'white' }}
            value={signupType}
            onChange={(e) => {
              isFiltering.current = true;
              setSignupType(e.target.value);
              setCurrentPage(1);
              setTableKey(prev => prev + 1); // Force table re-render
              
              // Clear the flag after a short delay
              setTimeout(() => {
                isFiltering.current = false;
              }, 200);
            }}
            options={signupTypeOptions}
            placeholder={t(user.list.signup_type_option)}
          />

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="startDate" className="m-0 text-white">
              {t(user.list.table.start_date_label)}
            </CFormLabel>
            <CFormInput
              ref={startDateInputRef}
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleDateChange('start', e.target.value)}
              onBlur={(e) => handleDateBlur('start', e)}
              onClick={(e) => handleDateClick('start', e)}
              className="form-control same-input-cls"
              style={{ 
                width: '155px',
                backgroundColor: '#212631',
                borderColor: '#212631',
                color: 'white'
              }}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="endDate" className="m-0 text-white">
              {t(user.list.table.end_date_label)}
            </CFormLabel>
            <CFormInput
              ref={endDateInputRef}
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => handleDateChange('end', e.target.value)}
              onBlur={(e) => handleDateBlur('end', e)}
              onClick={(e) => handleDateClick('end', e)}
              min={startDate}
              className="form-control same-input-cls"
              style={{ 
                width: '155px',
                backgroundColor: '#212631',
                borderColor: '#212631',
                color: 'white',
                marginRight: '80px'
              }}
            />
          </div>
          
          {(tempSearchText || adminStatus || userStatus || signupType || startDate || endDate) && (
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                type="button"
                onClick={handleClearFilter}
                className="filter-field-space"
                style={{ backgroundColor: '#212631', borderColor: '#212631' }}
              >
                {t(user.list.clear_filter_btn_text)}
              </CButton>
            </div>
          )}

          <CButton
            color="primary"
            type="button"
            onClick={handleGrantPremiumRewardClick}
            disabled={selectedIds.size === 0}
            className="filter-field-space"
          >
            {t(user.list.premium_reward.button_text)}
          </CButton>
        </CCol>
      </CRow>

      {/* Table */}
      <TNTable
        key={tableKey}
        columns={columns}
        data={userList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          // Prevent pagination changes during filtering operations
          if (isFiltering.current) {
            return;
          }
          
          setCurrentPage(pageIndex + 1);
          // Scroll to top of the page when pagination changes
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }}
        t={t}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
      />

    </>
  );
};

// Static breadcrumb
ListUserPage.breadCrumbArray = [
  {
    name: breadcrumb_key.userMaster?.list, // Using translation key instead of hardcoded text
    pathname: userMaster.list,
    active: true,
  }
];



export default ListUserPage;
